import React from 'react';
import { Clock, Upload, GitBranch, FileText, User } from 'lucide-react';

const ActivityFeed = ({ activities = [], onViewAll, isLoading = false }) => {
  const getActivityIcon = (type) => {
    switch (type) {
      case 'document_upload':
        return Upload;
      case 'change_request':
        return GitBranch;
      default:
        return FileText;
    }
  };

  const getActivityColor = (type, status) => {
    switch (type) {
      case 'document_upload':
        return 'text-green-600';
      case 'change_request':
        return status === 'pending' ? 'text-yellow-600' : 'text-blue-600';
      default:
        return 'text-gray-600';
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInSeconds = Math.floor((now - time) / 1000);

    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}m ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours}h ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days}d ago`;
    }
  };

  if (isLoading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Recent Activity</h3>
        <div className="space-y-4">
          {[...Array(5)]?.map((_, index) => (
            <div key={index} className="flex items-start space-x-3 animate-pulse">
              <div className="w-8 h-8 bg-muted rounded-full"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Recent Activity</h3>
        {activities?.length > 0 && (
          <button
            onClick={onViewAll}
            className="text-sm text-primary hover:text-primary/80 transition-colors"
          >
            View All
          </button>
        )}
      </div>
      <div className="space-y-4">
        {activities?.length > 0 ? (
          activities?.map((activity) => {
            const IconComponent = getActivityIcon(activity?.type);
            const iconColor = getActivityColor(activity?.type, activity?.status);

            return (
              <div key={activity?.id} className="flex items-start space-x-3">
                <div className={`p-2 rounded-full bg-muted ${iconColor}`}>
                  <IconComponent className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {activity?.title}
                  </p>
                  <div className="flex items-center space-x-2 mt-1">
                    <div className="flex items-center space-x-1">
                      <User className="w-3 h-3 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">
                        {activity?.user}
                      </span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Clock className="w-3 h-3 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">
                        {formatTimeAgo(activity?.timestamp)}
                      </span>
                    </div>
                  </div>
                  {activity?.status && (
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mt-2 ${
                      activity?.status === 'pending' ?'bg-yellow-100 text-yellow-800' 
                        : activity?.status === 'approved' ?'bg-green-100 text-green-800' :'bg-gray-100 text-gray-800'
                    }`}>
                      {activity?.status?.charAt(0)?.toUpperCase() + activity?.status?.slice(1)}
                    </span>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-8">
            <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm font-medium text-foreground mb-1">No recent activity</p>
            <p className="text-xs text-muted-foreground">
              Activities will appear here as users upload documents and make changes
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityFeed;